import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import ConnString from "../../../../ConnString.js";
import ReportLoadingOverlay from "../ReportLoadingOverlay.jsx";

const Connstring2 = ConnString.connstring;
const REPORT_TYPES = ["Single period", "Tabular by Month"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DESCRIPTION_FIELDS = ["Description", "AccountTitle", "Account", "Particulars", "FSDescription", "Title", "Name", "Label", "Caption"];
const AMOUNT_FIELDS = ["Amount", "CurrentAmount", "Balance", "Actual", "MTD", "Total", "Value"];
const PERCENT_FIELDS = ["Percentage", "Percent", "Pct", "Ratio", "%"];
const numberFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatPeriodEnd = ({ month, year }) => new Date(year, month, 0).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });

const escapeHtml = (value) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const parsed = Number(value.replace(/,/g, "").replace(/%/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};
const findField = (row, aliases) => {
  const key = Object.keys(row || {}).find((candidate) => aliases.some((alias) => candidate.toLowerCase() === alias.toLowerCase()));
  return key ? row[key] : undefined;
};
const getDescription = (row) => {
  const known = findField(row, DESCRIPTION_FIELDS);
  if (known !== undefined && known !== null) return String(known).trim();
  const entry = Object.entries(row || {}).find(([, value]) => typeof value === "string" && toNumber(value) === null);
  return entry ? String(entry[1]).trim() : "";
};
const getAmount = (row, monthName) => {
  const knownNumber = toNumber(findField(row, [...AMOUNT_FIELDS, monthName]));
  if (knownNumber !== null) return knownNumber;
  const ignored = new Set([...DESCRIPTION_FIELDS, ...PERCENT_FIELDS, "Sort", "Seq", "Sequence", "LineNo"].map((item) => item.toLowerCase()));
  const entry = Object.entries(row || {}).find(([key, value]) => !ignored.has(key.toLowerCase()) && toNumber(value) !== null);
  return entry ? toNumber(entry[1]) : 0;
};
const getPercentage = (row) => toNumber(findField(row, PERCENT_FIELDS));
const isTotalRow = (description, row) => {
  const explicit = findField(row, ["IsTotal", "TotalRow"]);
  const rowType = String(findField(row, ["RowType", "Type", "Format"]) || "").toLowerCase();
  return explicit === true || Number(explicit) === 1 || rowType.includes("total") || /^(total\b|gross profit|net income)/i.test(description);
};
const isSectionRow = (description, row, amount) => {
  const rowType = String(findField(row, ["RowType", "Type", "Format"]) || "").toLowerCase();
  return rowType.includes("header") || rowType.includes("section") || (amount === 0 && /^[A-Z][A-Z /&-]+$/.test(description));
};

const IncomeStatement = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const now = new Date();
  const [reportType, setReportType] = useState("Single period");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [parameters, setParameters] = useState(null);
  const [rows, setRows] = useState([]);
  const [reportMonths, setReportMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const compID = sessionStorage.getItem("compID");
  const companyName = sessionStorage.getItem("compName") || sessionStorage.getItem("selectedCompany") || "INFORMATION SYSTEMS AUDIT AND CONTROL ASSOCIATION (MANILA CHAPTER), INC.";
  const displayCompanyName = companyName.replace(/\s*\(ISACA\)\s*$/i, "");

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, index) => currentYear + 1 - index);
  }, []);
  const normalizedRows = useMemo(() => {
    const monthName = parameters ? MONTHS[parameters.month - 1] : MONTHS[month - 1];
    return rows.map((row, index) => {
      const description = getDescription(row);
      const amount = getAmount(row, monthName);
      return { id: index, description, amount, amounts: Array.isArray(row.Amounts) ? row.Amounts.map((value) => Number(value || 0)) : [amount], percentage: getPercentage(row), total: isTotalRow(description, row), section: isSectionRow(description, row, amount) };
    }).filter((row) => row.description);
  }, [rows, parameters, month]);

  const card = { bgcolor: dark ? "#182033" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,.08)" : "#e5eaf1"}`, borderRadius: "16px", boxShadow: dark ? "none" : "0 8px 28px rgba(15,23,42,.05)" };
  const muted = dark ? "#94a3b8" : "#64748b";

  const handleGenerate = async (event) => {
    event.preventDefault();
    setError("");
    if (!compID) {
      setError("Company information is missing. Please sign in again.");
      return;
    }
    const lastDay = new Date(year, month, 0).getDate();
    const nextParameters = { reportType, month, year, dateFrom: `${reportType === "Tabular by Month" ? 1 : month}/1/${year}`, dateTo: `${month}/${lastDay}/${year}` };
    setLoading(true);
    try {
      const response = await fetch(`${Connstring2}/api/Reports/ISACA/IncomeStatement`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ compID, ...nextParameters }),
      });
      const data = await response.json();
      if (!response.ok || data.success === false) throw new Error(data.message || "Unable to generate the report.");
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setReportMonths(Array.isArray(data.months) ? data.months : [MONTHS[month - 1]]);
      setParameters(nextParameters);
    } catch (err) {
      setRows([]);
      setReportMonths([]);
      setParameters(null);
      setError(err.message || "Unable to generate the report.");
    } finally {
      setLoading(false);
    }
  };

  const printReport = () => {
    if (!parameters || !normalizedRows.length) return;
    const printWindow = window.open("", "_blank", "width=900,height=900");
    if (!printWindow) {
      setError("Please allow pop-ups to print the Income Statement.");
      return;
    }
    const tabular = parameters.reportType === "Tabular by Month";
    const columnCount = tabular ? reportMonths.length + 3 : 3;
    const reportRows = normalizedRows.map((row) => {
      if (row.section) return `<tr class="section"><td colspan="${columnCount}">${escapeHtml(row.description)}</td></tr>`;
      const monthlyCells = tabular ? row.amounts.map((amount) => `<td class="num">${amount ? escapeHtml(numberFormatter.format(amount)) : ""}</td>`).join("") : "";
      return `<tr class="${row.total ? "total" : "detail"}"><td>${escapeHtml(row.description)}</td>${monthlyCells}<td class="num">${escapeHtml(numberFormatter.format(row.amount))}</td><td class="num">${row.percentage === null ? "" : `${escapeHtml(numberFormatter.format(row.percentage))}%`}</td></tr>`;
    }).join("");
    const headings = tabular
      ? `<td></td>${reportMonths.map((name) => `<td>${escapeHtml(name)}</td>`).join("")}<td>TOTAL</td><td>%</td>`
      : `<td></td><td>${escapeHtml(MONTHS[parameters.month - 1])}</td><td>%</td>`;
    printWindow.document.write(`<!doctype html><html><head><title>Profit and Loss - ${MONTHS[parameters.month - 1]} ${parameters.year}</title><style>@page{size:A4 ${tabular ? "landscape" : "portrait"};margin:10mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#111;margin:0;font-size:${tabular ? "8.5" : "11"}px}h1{font-size:22px;margin:0 0 2px}.company{font-size:11px;font-weight:700}.period{font-size:10px;font-weight:700;margin:2px 0 20px}table{width:${tabular ? "100%" : "500px"};border-collapse:collapse}.head td{font-weight:700;text-align:right;padding:4px}.head td:first-child{text-align:left}.section td{font-size:${tabular ? "10" : "13"}px;font-weight:800;padding:12px 0 3px}.detail td{padding:2px 3px;border-bottom:1px dotted #bbb}.detail td:first-child{padding-left:12px;white-space:nowrap}.num{text-align:right}.total td{font-weight:800;padding:3px}.total td.num{border-top:1px solid #111;border-bottom:1px solid #999}.footer{font-size:8px;color:#555;margin-top:20px}</style></head><body><h1>PROFIT AND LOSS</h1><div class="company">${escapeHtml(displayCompanyName.toUpperCase())}</div><div class="period">${tabular ? `For the Year ${parameters.year}` : `For the Month of ${escapeHtml(formatPeriodEnd(parameters))}`}</div><table><thead><tr class="head">${headings}</tr></thead><tbody>${reportRows}</tbody></table><div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };

  const reportFileName = () => `ISACA-Income-Statement-${parameters.year}-${String(parameters.month).padStart(2, "0")}`;

  const downloadExcel = () => {
    if (!parameters || !normalizedRows.length) return;
    const monthName = MONTHS[parameters.month - 1];
    const tabular = parameters.reportType === "Tabular by Month";
    const headings = tabular ? ["", ...reportMonths, "TOTAL", "%"] : ["", monthName, "%"];
    const sheetRows = [
      ["PROFIT AND LOSS"],
      [displayCompanyName.toUpperCase()],
      [tabular ? `For the Year ${parameters.year}` : `For the Month of ${formatPeriodEnd(parameters)}`],
      [],
      headings,
      ...normalizedRows.map((row) => row.section
        ? [row.description]
        : tabular
          ? [row.description, ...row.amounts, row.amount, row.percentage === null ? "" : row.percentage / 100]
          : [row.description, row.amount, row.percentage === null ? "" : row.percentage / 100]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);
    const lastColumn = headings.length - 1;
    worksheet["!cols"] = [{ wch: 42 }, ...headings.slice(1).map(() => ({ wch: 15 }))];
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastColumn } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: lastColumn } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: lastColumn } },
    ];
    normalizedRows.forEach((row, index) => {
      const excelRow = index + 6;
      if (!row.section) {
        const amountColumns = tabular ? Array.from({ length: reportMonths.length + 1 }, (_, column) => column + 1) : [1];
        amountColumns.forEach((column) => {
          const amountCell = worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: column })];
          if (amountCell) amountCell.z = "#,##0.00;[Red]-#,##0.00";
        });
        const percentCell = worksheet[XLSX.utils.encode_cell({ r: excelRow - 1, c: lastColumn })];
        if (percentCell && typeof percentCell.v === "number") percentCell.z = "0.00%";
      }
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Income Statement");
    XLSX.writeFile(workbook, `${reportFileName()}.xlsx`);
  };

  const downloadPdf = () => {
    if (!parameters || !normalizedRows.length) return;
    const tabular = parameters.reportType === "Tabular by Month";
    const pdf = new jsPDF({ orientation: tabular ? "landscape" : "portrait", unit: "mm", format: "a4" });
    const left = tabular ? 10 : 18;
    const percentX = tabular ? 287 : 180;
    const totalX = tabular ? 264 : 145;
    const monthPositions = tabular ? reportMonths.map((_, index) => 86 + (index * (154 / Math.max(reportMonths.length - 1, 1)))) : [];
    const lineHeight = tabular ? 4.7 : 5.2;
    const pageBottom = tabular ? 195 : 278;
    let y = 18;

    pdf.setTextColor(17, 24, 39);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text("PROFIT AND LOSS", left, y);
    y += 6;
    pdf.setFontSize(9.5);
    pdf.text(displayCompanyName.toUpperCase(), left, y);
    y += 4.5;
    pdf.setFontSize(8.5);
    pdf.text(tabular ? `For the Year ${parameters.year}` : `For the Month of ${formatPeriodEnd(parameters)}`, left, y);
    y += 11;
    pdf.setFontSize(9.5);
    if (tabular) reportMonths.forEach((name, index) => pdf.text(name, monthPositions[index], y, { align: "right" }));
    else pdf.text(MONTHS[parameters.month - 1], totalX, y, { align: "right" });
    if (tabular) pdf.text("TOTAL", totalX, y, { align: "right" });
    pdf.text("%", percentX, y, { align: "right" });
    y += 5;

    const addPage = () => {
      pdf.addPage();
      y = 18;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      if (tabular) reportMonths.forEach((name, index) => pdf.text(name, monthPositions[index], y, { align: "right" }));
      else pdf.text(`${MONTHS[parameters.month - 1]} ${parameters.year}`, totalX, y, { align: "right" });
      if (tabular) pdf.text("TOTAL", totalX, y, { align: "right" });
      pdf.text("%", percentX, y, { align: "right" });
      y += 5;
    };

    normalizedRows.forEach((row) => {
      if (y > pageBottom) addPage();
      if (row.section) {
        y += 4;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10.5);
        pdf.text(row.description, left, y);
        y += 4;
        return;
      }
      pdf.setFont("helvetica", row.total ? "bold" : "normal");
      pdf.setFontSize(9);
      pdf.text(row.description, row.total ? left : left + 4, y);
      if (tabular) row.amounts.forEach((amount, index) => {
        if (amount) pdf.text(numberFormatter.format(amount), monthPositions[index], y, { align: "right" });
      });
      pdf.text(numberFormatter.format(row.amount), totalX, y, { align: "right" });
      if (row.percentage !== null) pdf.text(`${numberFormatter.format(row.percentage)}%`, percentX, y, { align: "right" });
      if (row.total) {
        pdf.setLineWidth(0.25);
        pdf.line(tabular ? 72 : 108, y + 1.1, percentX, y + 1.1);
      } else {
        pdf.setDrawColor(190, 190, 190);
        pdf.setLineDashPattern([0.5, 0.7], 0);
        pdf.line(tabular ? 72 : 108, y + 1.1, percentX, y + 1.1);
        pdf.setLineDashPattern([], 0);
        pdf.setDrawColor(0, 0, 0);
      }
      y += lineHeight;
    });
    pdf.save(`${reportFileName()}.pdf`);
  };

  const isTabularReport = parameters?.reportType === "Tabular by Month";
  const reportGridColumns = isTabularReport
    ? `minmax(230px,1.8fr) repeat(${reportMonths.length}, minmax(105px,1fr)) minmax(115px,1fr) 75px`
    : "minmax(260px,1fr) 130px 80px";

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 2, md: 3 }, bgcolor: dark ? "#111827" : "#f7f8fc" }}>
      <ReportLoadingOverlay
        open={loading}
        reportName="Income Statement"
        period={`${MONTHS[month - 1]} ${year}`}
        detail={reportType}
      />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center"><Box sx={{ width: 46, height: 46, display: "grid", placeItems: "center", borderRadius: "13px", bgcolor: dark ? "#6366f122" : "#eef2ff", color: "#5b5ce2" }}><AssessmentOutlinedIcon /></Box><Box><Typography fontSize={{ xs: 24, md: 29 }} fontWeight={800}>Income Statement</Typography><Typography color={muted} fontSize={13}>ISACA Financial Report</Typography></Box></Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button disabled={!normalizedRows.length || loading} onClick={downloadExcel} variant="outlined" startIcon={<TableViewOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>Excel</Button>
          <Button disabled={!normalizedRows.length || loading} onClick={downloadPdf} variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>PDF</Button>
          <Button disabled={!normalizedRows.length || loading} onClick={printReport} variant="outlined" startIcon={<PrintOutlinedIcon />} sx={{ textTransform: "none", borderRadius: "10px" }}>Print</Button>
        </Stack>
      </Stack>
      <Paper component="form" onSubmit={handleGenerate} elevation={0} sx={{ ...card, p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2.5}><CalendarMonthOutlinedIcon sx={{ color: "#6366f1", fontSize: 21 }} /><Typography fontSize={16} fontWeight={800}>Report parameters</Typography></Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(220px,1.4fr) minmax(170px,1fr) minmax(140px,.8fr) auto" }, gap: 2, alignItems: "end" }}>
          <TextField select label="Type of report" value={reportType} onChange={(event) => setReportType(event.target.value)} size="small">{REPORT_TYPES.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}</TextField>
          <TextField select label="Month" value={month} onChange={(event) => setMonth(Number(event.target.value))} size="small">{MONTHS.map((name, index) => <MenuItem key={name} value={index + 1}>{name}</MenuItem>)}</TextField>
          <TextField select label="Year" value={year} onChange={(event) => setYear(Number(event.target.value))} size="small">{years.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}</TextField>
          <Button type="submit" disabled={loading} variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AssessmentOutlinedIcon />} sx={{ minHeight: 40, px: 2.5, bgcolor: "#5b5ce2", textTransform: "none", borderRadius: "10px", boxShadow: "none", whiteSpace: "nowrap" }}>{loading ? "Generating…" : "Generate report"}</Button>
        </Box>
      </Paper>
      {error && <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}
      <Paper elevation={0} sx={{ ...card, minHeight: 360, p: { xs: 2, md: 4 }, overflowX: "auto" }}>
        {parameters && normalizedRows.length ? (
          <Box sx={{ width: "100%", minWidth: isTabularReport ? 1050 : 0, maxWidth: isTabularReport ? 1500 : 720, mx: "auto", color: dark ? "#f8fafc" : "#111" }}>
            <Typography fontSize={25} fontWeight={900} lineHeight={1.1}>PROFIT AND LOSS</Typography>
            <Typography fontSize={11.5} fontWeight={800} mt={0.5}>{displayCompanyName.toUpperCase()}</Typography>
            <Typography fontSize={11} fontWeight={700} mb={2.5}>{isTabularReport ? `For the Year ${parameters.year}` : `For the Month of ${formatPeriodEnd(parameters)}`}</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: reportGridColumns, px: 1, pb: 0.7 }}>
              <Box />
              {isTabularReport ? reportMonths.map((name) => <Typography key={name} textAlign="right" fontSize={12} fontWeight={800}>{name}</Typography>) : <Typography textAlign="right" fontSize={12} fontWeight={800}>{MONTHS[parameters.month - 1]}</Typography>}
              {isTabularReport && <Typography textAlign="right" fontSize={12} fontWeight={800}>TOTAL</Typography>}
              <Typography textAlign="right" fontSize={12} fontWeight={800}>%</Typography>
            </Box>
            {normalizedRows.map((row) => row.section ? <Typography key={row.id} fontSize={14} fontWeight={900} mt={2.2} mb={0.4}>{row.description}</Typography> : (
              <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: reportGridColumns, alignItems: "end", px: 1, py: 0.35, ml: row.total ? 0 : 1.5, fontWeight: row.total ? 900 : 400 }}>
                <Typography fontSize={12.5} fontWeight="inherit">{row.description}</Typography>
                {isTabularReport && row.amounts.map((amount, index) => <Typography key={`${row.id}-${reportMonths[index]}`} textAlign="right" fontSize={12} fontWeight="inherit" sx={{ borderBottom: row.total ? `1px solid ${dark ? "#94a3b8" : "#111"}` : `1px dotted ${dark ? "#475569" : "#cbd5e1"}` }}>{amount ? numberFormatter.format(amount) : ""}</Typography>)}
                <Typography textAlign="right" fontSize={12.5} fontWeight="inherit" sx={{ borderBottom: row.total ? `1px solid ${dark ? "#94a3b8" : "#111"}` : `1px dotted ${dark ? "#475569" : "#cbd5e1"}` }}>{numberFormatter.format(row.amount)}</Typography>
                <Typography textAlign="right" fontSize={12.5} fontWeight="inherit" sx={{ borderBottom: row.total ? `1px solid ${dark ? "#94a3b8" : "#111"}` : `1px dotted ${dark ? "#475569" : "#cbd5e1"}` }}>{row.percentage === null ? "" : `${numberFormatter.format(row.percentage)}%`}</Typography>
              </Box>
            ))}
          </Box>
        ) : <Box sx={{ minHeight: 290, display: "grid", placeItems: "center", textAlign: "center" }}><Box><AssessmentOutlinedIcon sx={{ fontSize: 48, color: dark ? "#475569" : "#cbd5e1", mb: 1 }} /><Typography fontWeight={800}>Select report parameters</Typography><Typography color={muted} fontSize={13}>Choose the report type, month, and year, then generate the report.</Typography></Box></Box>}
      </Paper>
    </Box>
  );
};

export default IncomeStatement;
